import React, { lazy, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import theme from './theme/theme';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { createTenantTheme } from './theme/createTenantTheme';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThreadsProvider } from './contexts/ThreadsContext';
import { useAuth } from './hooks/useAuth';
import { SignIn, SignUp, ProfileClaiming } from './components/auth';
import { AppLayout } from './components/layout';
import { LimitedAccessBanner } from './components/shared/LimitedAccessBanner';
import ErrorBoundary from './components/shared/ErrorBoundary';
import MarketingApp from './marketing';
import PlatformConsole from './components/platform/PlatformConsole';
import { getLegacyPlatformRedirect, isMarketingHost, isPlatformAdminHost } from './config/tenant';
import OfflineIndicator from './components/shared/OfflineIndicator';
import SkipToContent from './components/shared/SkipToContent';
import KeyboardShortcutsDialog from './components/shared/KeyboardShortcutsDialog';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import AdminRoute from './components/shared/AdminRoute';
const SurveyPublishedPage = lazy(() => import('./pages/SurveyPublishedPage'));

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ChatsPage = lazy(() => import('./pages/ChatsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const BrowsePage = lazy(() => import('./pages/BrowsePage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const DuesPage = lazy(() => import('./pages/DuesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage'));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const SetAdminPasswordPage = lazy(() => import('./pages/SetAdminPasswordPage'));
const PublicEventPage = lazy(() => import('./pages/PublicEventPage'));
const VouchRedirectPage = lazy(() => import('./pages/VouchRedirectPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));
const MarketplaceCartPage = lazy(() => import('./pages/MarketplaceCartPage'));
const MarketplaceCheckoutPage = lazy(() => import('./pages/MarketplaceCheckoutPage'));
const MarketplacePaymentPage = lazy(() => import('./pages/MarketplacePaymentPage'));
const MarketplaceOrderDetailsPage = lazy(() => import('./pages/MarketplaceOrderDetailsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminMemberManagementPage = lazy(() => import('./pages/AdminMemberManagementPage'));
const AdminEventManagementPage = lazy(() => import('./pages/AdminEventManagementPage'));
const PaymentReportsPage = lazy(() => import('./pages/PaymentReportsPage'));
const PaymentSettingsPage = lazy(() => import('./pages/PaymentSettingsPage'));
const SavedPaymentMethodsPage = lazy(() => import('./pages/SavedPaymentMethodsPage'));
const EventApprovalsPage = lazy(() => import('./pages/EventApprovalsPage'));
const ApprovalsInboxPage = lazy(() => import('./pages/ApprovalsInboxPage'));
const WidgetTestPage = lazy(() => import('./pages/WidgetTestPage'));

// New Site Admin pages (consolidated from admin-web)
const EntitiesPage = lazy(() => import('./pages/admin/EntitiesPage'));
const EntityDetailPage = lazy(() => import('./pages/admin/EntityDetailPage'));
const EntitySettingsPage = lazy(() => import('./pages/admin/EntitySettingsPage'));
const EventsAdminPage = lazy(() => import('./pages/admin/EventsAdminPage'));
const SurveysAdminPage = lazy(() => import('./pages/admin/SurveysAdminPage'));
const ThreadsAdminPage = lazy(() => import('./pages/admin/ThreadsAdminPage'));
const ProfileClaimsPage = lazy(() => import('./pages/admin/ProfileClaimsPage'));
const ImportRequestsPage = lazy(() => import('./pages/admin/ImportRequestsPage'));
const JoinRequestsPage = lazy(() => import('./pages/admin/JoinRequestsPage'));
const OrgRequestsPage = lazy(() => import('./pages/admin/OrgRequestsPage'));
const MemberImportPage = lazy(() => import('./pages/admin/MemberImportPage'));
const BillingPage = lazy(() => import('./pages/admin/BillingPage'));
const PaymentRequestsPage = lazy(() => import('./pages/admin/PaymentRequestsPage'));
const PayRequestPage = lazy(() => import('./pages/PayRequestPage'));
const TenantsAdminPage = lazy(() => import('./pages/admin/TenantsAdminPage'));
const TenantDetailPage = lazy(() => import('./pages/admin/TenantDetailPage'));
const HierarchyWizardPage = lazy(() => import('./pages/admin/HierarchyWizardPage'));
const DuesManagementPage = lazy(() => import('./pages/admin/DuesManagementPage'));
const CommunityBrandingPage = lazy(() => import('./pages/admin/CommunityBrandingPage'));
const SalesforceImportPage = lazy(() => import('./pages/admin/SalesforceImportPage'));
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage'));
const PushNotificationsPage = lazy(() => import('./pages/admin/PushNotificationsPage'));
const PlatformFeesPage = lazy(() => import('./pages/admin/PlatformFeesPage'));
const AnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'));
const WidgetAnalyticsPage = lazy(() => import('./pages/admin/WidgetAnalyticsPage'));
const AdminWidgetTestPage = lazy(() => import('./pages/admin/WidgetTestPage'));
const MarketplaceAnalyticsPage = lazy(() => import('./pages/admin/MarketplaceAnalyticsPage'));
const MarketplaceCategoriesPage = lazy(() => import('./pages/admin/MarketplaceCategoriesPage'));
const MarketplaceFeesPage = lazy(() => import('./pages/admin/MarketplaceFeesPage'));
const FundraisersAdminPage = lazy(() => import('./pages/admin/FundraisersPage'));
const VotingAdminPage = lazy(() => import('./pages/admin/VotingPage'));
const VerificationManagementPage = lazy(() => import('./pages/admin/VerificationManagementPage'));
const ChapterRequestsPage = lazy(() => import('./pages/admin/ChapterRequestsPage'));
const FundraisersPage = lazy(() => import('./pages/FundraisersPage'));
const VotingPage = lazy(() => import('./pages/VotingPage'));
const FilesPage = lazy(() => import('./pages/FilesPage'));

const EventTicketingGuidePage = lazy(() => import('./pages/EventTicketingGuidePage'));

const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'));

const NewslettersPage = lazy(() => import('./pages/NewslettersPage'));
const ManageNewslettersPage = lazy(() => import('./pages/admin/ManageNewslettersPage'));
const NewsletterWizardPage = lazy(() => import('./pages/admin/NewsletterWizardPage'));
const NewsletterTemplatesPage = lazy(() => import('./pages/admin/NewsletterTemplatesPage'));
const NewsletterTemplatePreviewPage = lazy(
  () => import('./pages/admin/NewsletterTemplatePreviewPage'),
);
const NewsletterTemplateEditorPage = lazy(
  () => import('./pages/admin/NewsletterTemplateEditorPage'),
);
const NewsletterSavedBlocksPage = lazy(() => import('./pages/admin/NewsletterSavedBlocksPage'));
const NewsletterSavedBlockEditorPage = lazy(
  () => import('./pages/admin/NewsletterSavedBlockEditorPage'),
);
const NewsletterIssuePage = lazy(() => import('./pages/NewsletterIssuePage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const AnnouncementEditorPage = lazy(() => import('./pages/AnnouncementEditorPage'));
const AnnouncementDetailPage = lazy(() => import('./pages/AnnouncementDetailPage'));
const SurveysPage = lazy(() => import('./pages/SurveysPage'));
const SurveyEditPage = lazy(() => import('./pages/SurveyEditPage'));
const SurveyResultsPage = lazy(() => import('./pages/SurveyResultsPage'));
const SurveyResponsePage = lazy(() => import('./pages/SurveyResponsePage'));
const PublicSurveyPage = lazy(() => import('./pages/PublicSurveyPage'));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <CircularProgress />
  </Box>
);

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  // AuthContext will handle redirection to /claim-profile if needed
  return <>{children}</>;
};

// Public Route wrapper (redirects to home if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (user) {
    return <Navigate to={isPlatformAdminHost() ? '/admin' : '/home'} replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  const navigate = useNavigate();
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Global keyboard shortcuts
  useKeyboardNavigation([
    {
      key: '1',
      altKey: true,
      action: () => navigate('/home'),
      description: 'Go to Home',
    },
    {
      key: '2',
      altKey: true,
      action: () => navigate('/chats'),
      description: 'Go to Chats',
    },
    {
      key: '3',
      altKey: true,
      action: () => navigate('/events'),
      description: 'Go to Events',
    },
    {
      key: '4',
      altKey: true,
      action: () => navigate('/browse'),
      description: 'Go to Browse',
    },
    {
      key: '5',
      altKey: true,
      action: () => navigate('/directory'),
      description: 'Go to Community',
    },
    {
      key: '6',
      altKey: true,
      action: () => navigate('/admin'),
      description: 'Go to Admin',
    },
    {
      key: '7',
      altKey: true,
      action: () => navigate('/settings'),
      description: 'Go to Settings',
    },
    {
      key: '?',
      action: () => setShortcutsOpen(true),
      description: 'Show keyboard shortcuts',
    },
  ]);

  return (
    <>
      <SkipToContent />
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/signin"
          element={
            <PublicRoute>
              <SignIn />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUp />
            </PublicRoute>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <Suspense fallback={<LoadingFallback />}>
                <ForgotPasswordPage />
              </Suspense>
            </PublicRoute>
          }
        />
        {/* Org-owner set-password from the provisioning welcome email. Plain route
            (not PublicRoute) so it can sign the user in and route to the wizard. */}
        <Route
          path="/set-admin-password"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SetAdminPasswordPage />
            </Suspense>
          }
        />

        {/* Protected Routes with Layout */}
        <Route
          path="/claim-profile"
          element={
            <ProtectedRoute>
              <ProfileClaiming />
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <HomePage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <ChatsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <EventsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:eventId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <EventDetailPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/browse"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <BrowsePage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Files Route */}
        <Route
          path="/files"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <FilesPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Group Routes */}
        <Route
          path="/groups"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <GroupsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/:slug"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <GroupDetailPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/directory"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <DirectoryPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SearchResultsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dues"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <DuesPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fundraisers"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <FundraisersPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/voting"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <VotingPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/voting/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <VotingPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <MarketplacePage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace/cart"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <MarketplaceCartPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace/checkout"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <MarketplaceCheckoutPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace/payment"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <MarketplacePaymentPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace/orders/:orderId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <MarketplaceOrderDetailsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SettingsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <ProfilePage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <EditProfilePage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/account/delete"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <AccountDeletionPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes — Cognito admin/super_admin only */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminMemberManagementPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/members/:entityId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminMemberManagementPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/events"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <EventsAdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/entities"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <EntitiesPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/entities/:entityId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <EntityDetailPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/entity-settings/:entityId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <EntitySettingsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/surveys"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SurveysAdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ManageNewslettersPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/blocks/:id/edit"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterSavedBlockEditorPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/blocks"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterSavedBlocksPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/templates"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterTemplatesPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/templates/new"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterTemplateEditorPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/templates/:id/edit"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterTemplateEditorPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/templates/:id"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterTemplatePreviewPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/create"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterWizardPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletters/:id/edit"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <NewsletterWizardPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements/create"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AnnouncementEditorPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements/:id/edit"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AnnouncementEditorPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/threads"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ThreadsAdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        {/* Profile claims removed — replaced by open signup + vouch model */}
        <Route path="/admin/profile-claims" element={<Navigate to="/admin/members" replace />} />
        <Route
          path="/admin/import-requests"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ImportRequestsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/join-requests"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <JoinRequestsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/dues"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <DuesManagementPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/org-requests"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <OrgRequestsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/import-members"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <MemberImportPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/billing"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <BillingPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payment-requests"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PaymentRequestsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <TenantsAdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants/:orgId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <TenantDetailPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/setup-hierarchy"
          element={
            <ProtectedRoute>
              <AdminRoute>
                {/* Full-screen focused setup — no app header/menu (AppLayout) by design. */}
                <Suspense fallback={<LoadingFallback />}>
                  <HierarchyWizardPage />
                </Suspense>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pay/:id"
          element={
            <ProtectedRoute>
              {/* Focused payment screen — no app chrome. */}
              <Suspense fallback={<LoadingFallback />}>
                <PayRequestPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/branding"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <CommunityBrandingPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/salesforce-import"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <SalesforceImportPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        {/* User management consolidated into /admin/members */}
        <Route path="/admin/user-management" element={<Navigate to="/admin/members" replace />} />
        <Route
          path="/admin/push-notifications"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PushNotificationsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/platform-fees"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PlatformFeesPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AnalyticsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/widget-analytics"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <WidgetAnalyticsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/widget-test"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminWidgetTestPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/marketplace-analytics"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <MarketplaceAnalyticsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/marketplace-categories"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <MarketplaceCategoriesPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/marketplace-fees"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <MarketplaceFeesPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/fundraisers"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <FundraisersAdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/voting"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <VotingAdminPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/verification"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <VerificationManagementPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/chapter-requests"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ChapterRequestsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payment-reports"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PaymentReportsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/payment-settings/:entityId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <PaymentSettingsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-methods"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SavedPaymentMethodsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/event-approvals/:entityId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <EventApprovalsPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/approvals"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <ApprovalsInboxPage />
                  </Suspense>
                </AppLayout>
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/widget-test"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <WidgetTestPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Newsletter Routes */}
        <Route
          path="/newsletters"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <NewslettersPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Announcement Routes */}
        <Route
          path="/announcements"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <AnnouncementsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/announcements/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <AnnouncementDetailPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Survey Routes */}
        <Route
          path="/surveys"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SurveysPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys/published/:surveyId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SurveyPublishedPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys/results/:surveyId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SurveyResultsPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys/edit/:surveyId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SurveyEditPage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Public Survey Page (no authentication required) - must be before :slug */}
        <Route
          path="/surveys/public/:slug"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicSurveyPage />
            </Suspense>
          }
        />
        <Route
          path="/surveys/:slug"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Suspense fallback={<LoadingFallback />}>
                  <SurveyResponsePage />
                </Suspense>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Public Ticketing Guide Page (no authentication required) */}
        <Route
          path="/ticketing-guide"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <EventTicketingGuidePage />
            </Suspense>
          }
        />

        {/* Newsletter web issue — the destination for digest deep links.
            Unauthenticated so public issues resolve; the page asks for sign-in
            when an issue is members-only. */}
        <Route
          path="/newsletters/:slug"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <NewsletterIssuePage />
            </Suspense>
          }
        />

        {/* Unsubscribe landing (never behind auth — it's in every email) */}
        <Route
          path="/u/:token"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <UnsubscribePage />
            </Suspense>
          }
        />

        {/* Public Event Page (no authentication required) */}
        <Route
          path="/events/public/:slug"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicEventPage />
            </Suspense>
          }
        />

        {/* Vouch deep link redirect */}
        <Route
          path="/vouch/:memberId"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <VouchRedirectPage />
            </Suspense>
          }
        />

        {/* Redirect root to home — or straight to admin on the platform console */}
        <Route
          path="/"
          element={<Navigate to={isPlatformAdminHost() ? '/admin' : '/home'} replace />}
        />

        {/* 404 Page */}
        <Route
          path="*"
          element={
            <AppLayout>
              <Suspense fallback={<LoadingFallback />}>
                <NotFoundPage />
              </Suspense>
            </AppLayout>
          }
        />
      </Routes>
    </>
  );
}

function ThemedApp() {
  const { config, isLoading } = useTenant();
  const tenantTheme = isLoading ? theme : createTenantTheme(config.branding);

  return (
    <ThemeProvider theme={tenantTheme}>
      <CssBaseline />
      <OfflineIndicator />
      <Router>
        <AuthProvider>
          <NotificationProvider>
            <ThreadsProvider>
              <PlatformConsole>
                <AppContent />
              </PlatformConsole>
            </ThreadsProvider>
          </NotificationProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  // Legacy platform host (thecrest.app / *.thecrest.app) → same URL on konklave.app.
  const legacyRedirect = getLegacyPlatformRedirect();
  if (legacyRedirect) {
    window.location.replace(legacyRedirect);
    return null;
  }

  // Marketing hosts (konklave.app / www) render the platform landing page instead
  // of a tenant member app — no tenant resolution or auth.
  if (isMarketingHost()) {
    return (
      <ErrorBoundary>
        <MarketingApp />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <TenantProvider>
        <ThemedApp />
      </TenantProvider>
    </ErrorBoundary>
  );
}

export default App;
